import { addSearchParams, FootnoteReferenceV2 } from '@oneaudi/feature-app-utils';
import {
  SYS_BORDER_RADIUS_SM,
  SYS_BREAKPOINT_MD,
  SYS_SPACE_FIXED_25,
} from '@oneaudi/unified-web-common';
import { Icon, Text, TextLink } from '@oneaudi/unified-web-components';
import React, { Fragment, memo, useContext, useMemo } from 'react';
import styled from 'styled-components';
import type { CompareCarline } from '../../context/CompareContext';
import { CompareContext } from '../../context/CompareContext';
import { createCompareCheckboxId } from '../../context/compareContextUtils';
import { useContent } from '../../context/ContentContext';
import { DataContext } from '../../context/DataContext';
import { useFeatureAppEnv } from '../../context/FeatureAppEnvContext';
import { useI18n } from '../../i18n';
import { useCompareTrackingManager } from '../../hooks/useCompareTrackingManager';

const BaseCard = styled.div`
  display: grid;
  column-gap: var(--spacing-relative-md);
  grid-template-columns: 104px auto;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    grid-template-columns: 125px auto;
  }
`;

const Card = styled(BaseCard)`
  grid-template-areas:
    'a b'
    'a c';
  grid-template-rows: auto 1fr;
`;

const ImagePlaceholder = styled.div`
  border-radius: ${() => (process.env.IS_LEGACY ? '0' : SYS_BORDER_RADIUS_SM)};
  background-color: ${({ theme }) => theme.colors.compareEmptyPlaceholderBackground};
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 4 / 3;
  width: 100%;

  & svg {
    color: ${({ theme }) => theme.colors.compareEmptyIconColor};
  }
`;

const EmptyItemText = styled(Text)`
  align-self: center;
`;

const CardImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;
const CardImageWrapper = styled.div`
  grid-area: a;
  width: 100%;
  height: auto;
  display: flex;
  align-items: center;
  border-radius: ${() => (process.env.IS_LEGACY ? '0' : SYS_BORDER_RADIUS_SM)};
  background-color: ${({ theme }) => theme.colors.compareEmptyPlaceholderBackground};
  aspect-ratio: 4/3;
`;

const TextWrapper = styled.div`
  grid-area: b;
`;

const CarName = styled(Text)`
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;

  @media (min-width: ${({ theme }) => theme.breakpoints.xl}px) {
    -webkit-line-clamp: 2;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
`;

const RemoveButton = styled(TextLink)`
  grid-area: c;
  align-self: end;
  margin-block-start: ${SYS_SPACE_FIXED_25};
  text-decoration: none;
`;

const EmptyImage = memo(function EmptyImage() {
  return (
    <ImagePlaceholder>
      <Icon size="l" name="car" />
    </ImagePlaceholder>
  );
});

const EmptyFlyoutItem = memo(function EmptyItem() {
  const text = useI18n('compare.noModelSelected');

  return (
    <BaseCard>
      <EmptyImage />
      <EmptyItemText
        variant={{
          order: '2',
          style: 'normal',
        }}
      >
        {text}
      </EmptyItemText>
    </BaseCard>
  );
});

export type PriceProps = {
  readonly value?: number;
};

export const Price = memo(function Price({ value }: PriceProps) {
  const { 'audi-number-formatter-service': numberFormatterService } =
    useFeatureAppEnv().featureServices;
  const {
    priceFootnotes,
    priceDisplayOption: { showBasePrice, showVat },
  } = useContent();

  if (!showBasePrice || value === undefined) {
    return null;
  }

  return (
    <Text
      variant={{
        order: '3',
        style: 'normal',
        type: 'copy',
      }}
      margin={[SYS_SPACE_FIXED_25, 0, 0, 0]}
    >
      {useI18n('priceFrom', { value: numberFormatterService.formatCurrency(value) })}
      {priceFootnotes?.map((footnote: string, i: number) => (
        <Fragment key={footnote}>
          {i > 0 && <sup>{', '}</sup>}
          <FootnoteReferenceV2
            footnoteId={footnote}
            footnoteAriaLabel="price"
            data-testid={`price-footnote_${i}`}
          />
        </Fragment>
      ))}
      {showVat && ` ${useI18n('vat')}`}
    </Text>
  );
});

export type FlyoutItemProps = {
  readonly carline?: CompareCarline;
};

export const FlyoutItem = memo(function Item({ carline }: FlyoutItemProps) {
  const { state, dispatch } = useContext(CompareContext);
  const { allCarlineGroups } = useContext(DataContext);
  const data = useMemo(
    () =>
      allCarlineGroups
        .flatMap(({ carlines }) => carlines)
        .find(({ identifier }) => identifier.id === carline?.id),
    [allCarlineGroups, carline],
  );
  const removeButtonText = useI18n('compare.removeButtonText');
  const { remove: trackRemove } = useCompareTrackingManager();

  if (!carline || !data) {
    return <EmptyFlyoutItem />;
  }

  const renderImage = () => {
    const image = data.exteriorViews?.side;
    if (!image) {
      return <EmptyImage />;
    }
    const getImg = (width: string) => addSearchParams(image, new Map([['wid', width]]));
    const minImg = getImg('104');
    return (
      <CardImageWrapper>
        <CardImage
          width="104"
          height="78"
          alt=""
          src={minImg}
          srcSet={`${minImg} 104w,${getImg('125')} 125w,${getImg('250')} 250w`}
          sizes={`(min-width: ${SYS_BREAKPOINT_MD}) 125px,104px`}
        />
      </CardImageWrapper>
    );
  };

  return (
    <Card>
      {renderImage()}
      <TextWrapper>
        <CarName variant={{ order: process.env.IS_LEGACY ? '2' : '3', style: 'bold' }}>
          {data.name}
        </CarName>
        <Price value={data.prices.minPrice?.value} />
      </TextWrapper>
      <ButtonWrapper>
        <RemoveButton
          variant="standalone"
          size="sm"
          icon="remove-circle"
          iconPosition="leading"
          onClick={() => {
            if (state.carlines.length === 1) {
              const chkbxId = createCompareCheckboxId(carline.id);
              document.getElementById(chkbxId)?.focus();
            }
            trackRemove({ carline, label: removeButtonText, elementName: 'text link' });
            dispatch({ type: 'remove', carline });
          }}
        >
          {removeButtonText}
        </RemoveButton>
      </ButtonWrapper>
    </Card>
  );
});
